/**
 * Calendar's own side of combined search: `CalendarClient.searchEvents`
 * (wtfalch/calendar#17, landed in 0.2.0 for exactly this ticket, agora#216),
 * turned into rows Calendar opens by `date`/`eventId`.
 *
 * Returns the matched `CalendarEvent`s alongside the `SearchRow`s, the same
 * reason `mail.ts` returns its threads: `people.ts` derives the People group
 * from these events rather than searching calendar a second time.
 */
import type { CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';

import type { AgoraRoute } from '../routes.ts';
import type { SearchRow } from './types.ts';

export type CalendarSearchOutcome = {
  rows: readonly SearchRow[];
  events: readonly CalendarEvent[];
};

const DEFAULT_LIMIT = 20;

/** `start` is a local date-time with no offset (`CalendarEvent.start`'s own
 *  doc); its first ten characters are the `YYYY-MM-DD` `CalendarLocation.date`
 *  wants, the same slice `Calendar.tsx`'s own `dateParam` would produce for
 *  this event's day. */
function dateOf(event: CalendarEvent): string {
  return event.start.slice(0, 10);
}

function routeFor(agoraId: string, event: CalendarEvent): AgoraRoute {
  return {
    kind: 'agora',
    agoraId,
    section: 'calendar',
    location: { date: dateOf(event), eventId: event.id },
  };
}

export async function searchCalendarEvents(
  client: CalendarClient,
  agoraId: string,
  query: string,
  options?: { limit?: number },
): Promise<CalendarSearchOutcome> {
  // Errors are left to throw as whatever `searchEvents` throws (a
  // `CalendarError` against a live agora; `model.ts`'s `runSearch` is the one
  // place that turns it into a group's one-line message).
  const events = await client.searchEvents(query, { limit: options?.limit ?? DEFAULT_LIMIT });
  const rows: SearchRow[] = events.map((event) => ({
    id: event.id,
    title: event.title || '(untitled event)',
    snippet: event.location || event.description || undefined,
    time: event.start,
    dated: true,
    route: routeFor(agoraId, event),
  }));
  return { rows, events };
}
